import {
  type ComponentType,
  lazy,
  type LazyExoticComponent,
  Suspense,
} from 'react';
import { PosSpinner } from 'src/core/POS/PosSpinner';

interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Wraps React.lazy() with retry logic for failed module imports.
 *
 * Common on iOS devices where network conditions cause dynamic import failures.
 * Retries with exponential backoff before giving up.
 *
 * @example
 * ```tsx
 * const LazyFooter = lazyWithRetry(() =>
 *   import("src/components/Footer").then(m => ({ default: m.Footer }))
 * );
 * ```
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): LazyExoticComponent<T> => {
  const { maxRetries = 3, retryDelay = 1000 } = options;

  const retryImport = async (attempt = 0): Promise<{ default: T }> => {
    try {
      return await importFunc();
    } catch (error) {
      // If we've exhausted retries, throw the error
      if (attempt >= maxRetries) {
        console.error(
          `Failed to load module after ${maxRetries} retries:`,
          error
        );
        throw error;
      }
      // Wait with exponential backoff
      await new Promise<void>((resolve) => {
        setTimeout(resolve, retryDelay * Math.pow(2, attempt));
      });
      // Retry
      return retryImport(attempt + 1);
    }
  };

  return lazy(() => retryImport());
};

// Helper to wrap lazy components with Suspense
export const withSuspense = (Component: React.ComponentType) => {
  return (
    <Suspense fallback={<PosSpinner />}>
      <Component />
    </Suspense>
  );
};
